/**
 * The restaurant-owned side of the render input.
 *
 * These shapes are what the layout engine profiles and what template bindings
 * resolve against, so the paths here are effectively public API for template
 * authors: `item.name`, `category.itemCount`, `restaurant.phone` and so on.
 */
const mean = (values) => values.length === 0 ? 0 : values.reduce((sum, value) => sum + value, 0) / values.length;
export function profileCategory(category) {
    const items = category.items.filter((item) => item.available);
    const nameLengths = items.map((item) => item.name.length);
    const descriptions = items.map((item) => item.description?.trim() ?? '');
    const descriptionLengths = descriptions.map((text) => text.length);
    const prices = items.map((item) => item.price);
    const withImages = items.filter((item) => Boolean(item.image)).length;
    const withDescriptions = descriptions.filter((text) => text.length > 0).length;
    const avgDescription = mean(descriptionLengths);
    const avgName = mean(nameLengths);
    return {
        categoryId: category.id,
        itemCount: items.length,
        imageCoverage: items.length === 0 ? 0 : withImages / items.length,
        featuredCount: items.filter((item) => item.featured).length,
        avgNameLength: avgName,
        maxNameLength: Math.max(0, ...nameLengths),
        avgDescriptionLength: avgDescription,
        maxDescriptionLength: Math.max(0, ...descriptionLengths),
        descriptionCoverage: items.length === 0 ? 0 : withDescriptions / items.length,
        variantCount: items.reduce((sum, item) => sum + (item.variants?.length ?? 0), 0),
        priceSpread: prices.length === 0 ? 0 : Math.max(...prices) - Math.min(...prices),
        // A photo dominates an entry's height; a long description adds roughly a
        // line per 60 characters. Tuned against the bundled demo menus.
        density: 1 +
            (items.length === 0 ? 0 : withImages / items.length) * 1.8 +
            avgDescription / 60 +
            avgName / 90,
        hasCategoryImage: Boolean(category.image),
    };
}
export function profileMenu(menu) {
    const categories = menu.categories.map(profileCategory);
    const counts = categories.map((profile) => profile.itemCount);
    const totalItems = counts.reduce((sum, count) => sum + count, 0);
    const averageCount = mean(counts);
    const totalWithImages = menu.categories.reduce((sum, category) => sum + category.items.filter((item) => item.available && item.image).length, 0);
    const totalWithDescriptions = menu.categories.reduce((sum, category) => sum + category.items.filter((item) => item.available && item.description?.trim()).length, 0);
    return {
        totalItems,
        categoryCount: categories.length,
        imageCoverage: totalItems === 0 ? 0 : totalWithImages / totalItems,
        avgItemsPerCategory: averageCount,
        categorySizeVariance: counts.length === 0 ? 0 : Math.sqrt(mean(counts.map((count) => (count - averageCount) ** 2))),
        descriptionCoverage: totalItems === 0 ? 0 : totalWithDescriptions / totalItems,
        categories: Object.fromEntries(categories.map((profile) => [profile.categoryId, profile])),
    };
}
//# sourceMappingURL=menu.js.map